import { Form, Formik } from 'formik';
import React from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';

import useForumPostSuperPin from '../../../hooks/useForumPostSuperPin';
import { FORUM_POSTS_KEY, ForumPostSuperPin } from '../../../utils/backend-api/forumPosts';
import { TIModal } from '../../BaseUI';
import { CheckBoxField, TextAreaField } from '../../FormFields';
import styles from './styles.module.scss';

const RECIPIENT_TYPES = ['forum_subscribers', 'ti_subscribers', 'ti_leads', 'ti_canceled'];
const INITIAL_VALUES = {
  super_pin_preamble: '',
};
RECIPIENT_TYPES.map((type) => (INITIAL_VALUES[type] = false));

const validationSchema = () =>
  Yup.object({
    forum_subscribers: Yup.boolean(),
    ti_subscribers: Yup.boolean(),
    ti_leads: Yup.boolean(),
    ti_canceled: Yup.boolean(),
    super_pin_preamble: Yup.string(),
  }).test('recipientType', null, (values) => {
    const included_recipients = RECIPIENT_TYPES.map((type) => values[type])
      .filter(Boolean)
      .join(' ');
    if (included_recipients.length > 0) return true;

    return new Yup.ValidationError(
      'Recipients are required',
      null,
      RECIPIENT_TYPES[RECIPIENT_TYPES.length - 1]
    );
  });

export default function SuperPinModal({
  forumPostId,
  isOpen,
  setIsOpen,
}: {
  forumPostId: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { mutateAsync } = useForumPostSuperPin();
  const queryClient = useQueryClient();

  const handleSubmit = (values) => {
    const data: ForumPostSuperPin = {
      id: forumPostId,
      included_super_pin_recipients: Object.keys(values)
        .map((type) => RECIPIENT_TYPES.includes(type) && values[type] && type)
        .filter(Boolean)
        .join(' '),
      super_pin_preamble: values.super_pin_preamble,
    };

    return mutateAsync(data, {
      onSuccess: () => {
        toast.success('Post has been super pinned!');
        queryClient.invalidateQueries([FORUM_POSTS_KEY, forumPostId.toString()]);
        setIsOpen(false);
      },
      onError: (e) => {
        console.error(e);
        toast.error('Post was not super pinned, please try again.');
      },
    });
  };

  return (
    <>
      <TIModal
        isOpen={isOpen}
        disableScrollLock={false}
        onClose={() => setIsOpen(false)}
        parentClass={styles.superPinModal}
      >
        <Formik
          initialValues={INITIAL_VALUES}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, isValid }) => (
            <Form className="form">
              <label className={styles.superPinLabel}>
                Pin this post for the next 24 hours and send to the following recipients:
              </label>
              {RECIPIENT_TYPES.map((type) => (
                <CheckBoxField
                  key={type}
                  name={type}
                  label={type.replace(/_/g, ' ').replace('ti', 'TI')}
                  size="large"
                />
              ))}
              <TextAreaField
                name="super_pin_preamble"
                label="Preamble"
                autoComplete="off"
                minRows={3}
              />
              <div className={styles.formActions}>
                <button type="submit" className="button" disabled={isSubmitting || !isValid}>
                  Super Pin
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </TIModal>
    </>
  );
}
